<template>
  <TableSkeleton
    v-if="loading"
    :table-title="label"
    class="insights-table-skeleton"
  />
  <div v-else>
    <div
      v-if="emptyData"
      class="error-image no-data"
    >
      <NoFilterTableData />
      <h5>We weren't able to find the data you were filtering for.</h5>
    </div>
    <div
      v-else-if="error"
      class="error-image"
    >
      <NoDataAvailableTable />
    </div>
    <div v-else>
      <ChartCard
        :data-widget-id="downloadId"
      >
        <ChartHeader
          :widget-download-id="downloadId"
          :tooltip-key="tooltipKey"
          tooltip-placement="top"
          :label="label"
          :disabled="downloading"
          include-csv-download-option
          @download-csv="$emit('download-csv')"
        >
          <template #rightSide>
            <slot name="actions" />
          </template>
        </ChartHeader>
        <slot name="table">
          <InsightsTable
            :headers="headers"
            :rows="rows"
            :totals="totals"
            :show-passthrough-header="showPassthroughHeader"
            class="pa-4 pt-0"
          >
            <template v-for="header in headers" :key="header" #[`header:${header}`]>
              <slot :name="`header:${header}`" />
            </template>
            <template v-for="total in (totals || [])" :key="total.id" #[`total:${total.id}`]>
              <slot :name="`total:${total.id}`" />
            </template>
            <template #[`row:review`]>
              <slot :name="`row:review`" />
            </template>
            <template #[`row:screen`]>
              <slot :name="`row:screen`" />
            </template>
            <template #[`row:assessment`]>
              <slot :name="`row:assessment`" />
            </template>
            <template #[`row:offer`]>
              <slot :name="`row:offer`" />
            </template>
            <template #[`row:hired`]>
              <slot :name="`row:hired`" />
            </template>
          </InsightsTable>
        </slot>
      </ChartCard>
    </div>
  </div>
</template>

<script setup>
import InsightsTable from '@/components/insights/table/InsightsTable.vue';
import TableSkeleton from '@/components/insights/skeletonLoaders/TableSkeleton.vue';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import NoFilterTableData from '@/assets/no-filter-table-data.svg?component';
import NoDataAvailableTable from '@/assets/no-data-available-table.svg?component';

defineProps({
  label: {
    type: String,
    required: true,
  },
  headers: {
    type: Array,
    required: true,
  },
  rows: {
    type: Array,
    default: () => ([]),
  },
  totals: {
    type: Array,
    default: () => ([]),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  downloadId: {
    type: String,
    required: true,
  },
  tooltipKey: {
    type: String,
    required: true,
  },
  showPassthroughHeader: {
    type: Boolean,
    default: false,
  },
  downloading: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['download-csv']);
</script>

<style lang="less" scoped>
.error-image {
  background: @white;
  border-radius: 10px;
  height: 416px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  &.no-data {
    flex-direction: column;

    h5 {
      transform: translateY(-35px);
      color: @text-light;
    }
  }
}

.tables-container {
  background: @white;
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 16px;
}

.table-header  {
  width: 100%;
}

.title-corner {
  font-size: 16px;
  line-height: 20px;
  font-weight: 500;
}

.insights-table-skeleton {
  border-radius: 8px;
  border: 1px solid @stroke-default;
}
</style>
